import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormState } from 'final-form';

import { snackbar } from '@occc-app/design-system';
import { AudienceFragment, GeneralSurveyFragment } from '@occc/graphql';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { useCreateSurvey } from './useCreateSurvey';
import { useUpdateSurvey } from './useUpdateSurvey';

import { convertSurveyToInitialValues } from '../utils/convertSurveyToInitialValues';
import {
  isConfigureFormValuesValid,
  isScheduleFormValuesValid,
  isTargetFormValuesValid,
} from '../utils/isFormValuesValid';
import { SurveyTabs } from '../constants';
import { routeCreator } from '../routeCreator';
import {
  GeneralSurveyConfigureFormProps,
  GeneralSurveyConfigureFormValues,
} from '../components/ConfigureForm/types';
import { ConfigurationLayoutProps } from '../components/ConfigurationLayout';
import {
  GeneralSurveyTargetFormProps,
  GeneralSurveyTargetFormValues,
} from '../components/TargetForm/types';
import {
  GeneralSurveyScheduleFormProps,
  GeneralSurveyScheduleFormValues,
} from '../components/ScheduleForm/types';

type GetFormContainerPropsArgs = {
  surveyDetailsRoute: string;
  onCancel: (hasUnsavedChanges: boolean) => void;
  survey?: GeneralSurveyFragment;
  loading: boolean;
  chainId: string;
  audience?: AudienceFragment;
};

export const useGetFormContainerProps = ({
  onCancel,
  survey,
  loading,
  chainId,
  audience,
}: GetFormContainerPropsArgs) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const onCompleted = useCallback(
    ({ survey, isNew }: { survey: GeneralSurveyFragment; isNew: boolean }) => {
      navigate(routeCreator.details(survey.uuid));
      snackbar.success(`${survey.name} has been successfully ${isNew ? 'created' : 'updated'}.`);
    },
    [navigate],
  );

  const onError = useCallback(
    ({
      isIncentivizeError,
      survey,
    }: {
      isIncentivizeError: boolean;
      survey?: GeneralSurveyFragment;
    }) => {
      snackbar.error('Something went wrong. Please try again.');

      if (isIncentivizeError && survey?.uuid) {
        navigate(routeCreator.details(survey.uuid));
      }
    },
    [navigate],
  );
  const { configure, target, schedule } = useMemo(
    () => convertSurveyToInitialValues({ survey, audience }),
    [audience, survey],
  );

  const { createSurvey, loading: createSurveyLoading } = useCreateSurvey({ onError, onCompleted });
  const { updateSurvey, loading: updateSurveyLoading } = useUpdateSurvey({ onError, onCompleted });

  const configureFormStateRef = useRef<FormState<GeneralSurveyConfigureFormValues> | null>(null);
  const targetFormStateRef = useRef<FormState<GeneralSurveyTargetFormValues> | null>(null);
  const scheduleFormStateRef = useRef<FormState<GeneralSurveyScheduleFormValues> | null>(null);

  const tabValue = useMemo(() => {
    const tabValues = Object.values(SurveyTabs);
    const values = tabValues.filter((value) => pathname.endsWith(value));

    if (values.length > 0) {
      return values[0];
    }

    return SurveyTabs.Configure;
  }, [pathname]);

  const setTabValue = useCallback(
    (value: SurveyTabs) => {
      navigate(value);
    },
    [navigate],
  );

  const layoutProps: ConfigurationLayoutProps = useMemo(
    () => ({
      selectedTab: tabValue,
      onTabSelected: setTabValue,
    }),
    [setTabValue, tabValue],
  );

  const handleConfigureFormSubmit = useCallback<GeneralSurveyConfigureFormProps['onSubmit']>(
    (values, form) => {
      configureFormStateRef.current = form.getState();
      setTabValue(SurveyTabs.Target);
    },
    [setTabValue],
  );

  const onNextStepClick = useCallback(() => {
    if (survey) {
      if (tabValue === SurveyTabs.Configure) {
        setTabValue(SurveyTabs.Target);
        return;
      }

      if (tabValue === SurveyTabs.Target) {
        setTabValue(SurveyTabs.Schedule);
        return;
      }

      navigate(routeCreator.details(survey.uuid));
    }
  }, [navigate, setTabValue, survey, tabValue]);

  const isUserPermittedToEditSurvey = useIsUserPermittedTo(Feature.SURVEY_EDIT);

  const configureFormProps: GeneralSurveyConfigureFormProps = useMemo(
    () => ({
      surveyStatus: survey?.status,
      onSubmit: handleConfigureFormSubmit,
      initialValues: configureFormStateRef.current?.values || configure,
      onCancelClick: onCancel,
      isEntityExists: !!survey,
      isUserPermittedToEditSurvey,
      loading,
      onNextStepClick,
    }),
    [
      survey,
      handleConfigureFormSubmit,
      configure,
      onCancel,
      isUserPermittedToEditSurvey,
      loading,
      onNextStepClick,
    ],
  );

  const handleTargetFormSubmit = useCallback<GeneralSurveyTargetFormProps['onSubmit']>(
    (values, form) => {
      targetFormStateRef.current = form.getState();
      setTabValue(SurveyTabs.Schedule);
    },
    [setTabValue],
  );

  const targetFormProps: GeneralSurveyTargetFormProps = useMemo(
    () => ({
      surveyStatus: survey?.status,
      onSubmit: handleTargetFormSubmit,
      initialValues: targetFormStateRef.current?.values || target,
      onCancelClick: onCancel,
      isEntityExists: !!survey,
      isUserPermittedToEditSurvey,
      loading,
      chainId,
      onNextStepClick,
    }),
    [
      survey,
      handleTargetFormSubmit,
      target,
      onCancel,
      isUserPermittedToEditSurvey,
      loading,
      chainId,
      onNextStepClick,
    ],
  );

  const handleScheduleFormSubmit = useCallback<GeneralSurveyScheduleFormProps['onSubmit']>(
    async (values, form) => {
      scheduleFormStateRef.current = form.getState();
      const configureFormValues = configureFormStateRef.current?.values || configure;
      const targetFormValues = targetFormStateRef.current?.values || target;
      const scheduleFormValues = scheduleFormStateRef.current?.values || schedule;

      if (!isConfigureFormValuesValid(configureFormValues)) {
        setTabValue(SurveyTabs.Configure);
        return;
      }

      if (!isTargetFormValuesValid(targetFormValues)) {
        setTabValue(SurveyTabs.Target);
        return;
      }

      if (!isScheduleFormValuesValid(scheduleFormValues)) {
        return;
      }

      if (!survey) {
        await createSurvey({ configureFormValues, targetFormValues, scheduleFormValues });
        return;
      }

      await updateSurvey({
        configureFormValues,
        targetFormValues,
        scheduleFormValues,
        configureFormValuesDirty: configureFormStateRef.current?.dirty || true,
        targetFormValuesDirty: targetFormStateRef.current?.dirty || true,
        scheduleFormValuesDirty: scheduleFormStateRef.current?.dirty || true,
        configureFormDirtyFields: configureFormStateRef.current?.dirtyFields || {},
        survey,
      });
    },
    [configure, createSurvey, schedule, setTabValue, survey, target, updateSurvey],
  );

  const scheduleFormProps: GeneralSurveyScheduleFormProps = useMemo(
    () => ({
      onSubmit: handleScheduleFormSubmit,
      initialValues: scheduleFormStateRef.current?.values || schedule,
      onCancelClick: onCancel,
      isEntityExists: !!survey,
      loading: loading || createSurveyLoading || updateSurveyLoading,
      isUserPermittedToEditSurvey,
      onNextStepClick,
    }),
    [
      handleScheduleFormSubmit,
      schedule,
      onCancel,
      survey,
      loading,
      createSurveyLoading,
      updateSurveyLoading,
      isUserPermittedToEditSurvey,
      onNextStepClick,
    ],
  );

  return useMemo(
    () => ({
      configureFormProps,
      targetFormProps,
      scheduleFormProps,
      layoutProps,
    }),
    [configureFormProps, layoutProps, scheduleFormProps, targetFormProps],
  );
};
